"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useMemberUser } from "@/src/hooks/useMemberUser";
import { useOspApiClientProvider } from "@/src/hooks/useOspApiClientProvider";

import { fetchPlaidConnections } from "./memberPlaidApi";
import type { PlaidGateStatus } from "./plaidGateTypes";
import {
  resolvePlaidStatusFromConnections,
  resolvePlaidTrialExpiredUi,
} from "./plaidStatus";
import { PLAID_CONNECTIONS_QUERY_KEY } from "./usePlaidConnections";

export interface UsePlaidGateStatusOptions {
  /** 默认 true；需已登录且 memberApi 可用 */
  enabled?: boolean;
}

export interface UsePlaidGateStatusResult {
  data: PlaidGateStatus | undefined;
  /**
   * 到期提示开关：未付费 ∧ plaid_status=expired。
   * 未连接 / 已付费 / 加载或失败 → false（AC 降级不白屏）。
   */
  isExpired: boolean;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

/**
 * Plaid 试链门控态（be-design v5.0）。
 * 复用 `GET /financial/connections/PLAID`（与 `usePlaidConnections` 同 queryKey），
 * 组合 `plaid_status` + `useMemberUser` 会员态。
 */
export function usePlaidGateStatus(
  options?: UsePlaidGateStatusOptions,
): UsePlaidGateStatusResult {
  const { memberApi } = useOspApiClientProvider();
  const enabled = (options?.enabled ?? true) && Boolean(memberApi);
  const { data: memberPlanInfo, isLoading: isMemberLoading } = useMemberUser();

  const query = useQuery({
    queryKey: PLAID_CONNECTIONS_QUERY_KEY,
    enabled,
    queryFn: async () => {
      if (!memberApi) {
        throw new Error("memberApi is not available");
      }
      return fetchPlaidConnections(memberApi);
    },
    retry: false,
  });

  const isPaidMember = Boolean(memberPlanInfo?.isMember);
  const connectionsError = query.isError;
  const connections = connectionsError ? undefined : query.data;

  const gate = useMemo((): PlaidGateStatus | undefined => {
    if (connectionsError || connections === undefined) {
      return undefined;
    }
    if (isMemberLoading) {
      return undefined;
    }

    const plaidStatus = resolvePlaidStatusFromConnections(connections);
    const hasConnections = connections.length > 0;
    return {
      plaidStatus,
      hasConnections,
      is_expired: resolvePlaidTrialExpiredUi({
        isPaidMember,
        plaidStatus,
      }),
    };
  }, [connections, connectionsError, isMemberLoading, isPaidMember]);

  return {
    data: gate,
    isExpired: gate?.is_expired === true,
    isLoading: query.isLoading || isMemberLoading,
    isError: connectionsError,
    error: query.error instanceof Error ? query.error : null,
    refetch: query.refetch,
  };
}
